import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { sharedApi } from "@/lib/api";

const LS_KEY = "allsale_recently_viewed";
const MAX = 12;

function readIds() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr.filter(Boolean).slice(0, MAX) : [];
  } catch {
    return [];
  }
}

function writeIds(ids) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(ids.slice(0, MAX))); } catch { /* ignore */ }
}

/**
 * Track a listing ID as "just viewed" (called from ad-detail on mount).
 * De-dupes and keeps the most-recent MAX entries at the head.
 */
export function pushRecent(id) {
  if (!id) return;
  const cur = readIds();
  const next = [id, ...cur.filter((x) => x !== id)].slice(0, MAX);
  writeIds(next);
  // Nudge listeners in the same tab
  try { window.dispatchEvent(new StorageEvent("storage", { key: LS_KEY })); } catch { /* ignore */ }
}

/**
 * Read the current recently-viewed IDs (with live cross-tab updates).
 */
export function useRecentlyViewedIds() {
  const [ids, setIds] = useState(readIds);
  useEffect(() => {
    const sync = () => setIds(readIds());
    window.addEventListener("storage", sync);
    return () => window.removeEventListener("storage", sync);
  }, []);
  return ids;
}

/**
 * Fetch the listing objects for the current IDs (preserves the order).
 * Returns { listings: [...], isLoading, isError }.
 */
export function useRecentlyViewed() {
  const ids = useRecentlyViewedIds();

  const q = useQuery({
    queryKey: ["recently-viewed", ids.join(",")],
    queryFn: async () => {
      if (ids.length === 0) return [];
      const { data } = await sharedApi.get("/classifieds/listings", {
        params: { ids: ids.join(","), country: "NZ", limit: MAX },
      });
      const returned = data?.listings || [];
      // Sort into the IDs order (server may re-order)
      const byId = new Map(returned.map((l) => [l.id, l]));
      return ids.map((id) => byId.get(id)).filter(Boolean);
    },
    enabled: ids.length > 0,
    staleTime: 60_000,
    retry: 0,
  });

  return { listings: q.data || [], isLoading: q.isLoading, isError: q.isError };
}
